import React from "react";
import { Container } from "../layout/Container";

const capabilities = [
  {
    icon: "calculate",
    title: "Container Selection Intelligence",
    description:
      "Most tools pack cargo into containers you've already chosen. We find the cheapest combination of container types that fits everything — before you book a single slot.",
    linkLabel: "See how it works",
    linkHref: "#how-it-works",
  },
  {
    icon: "view_in_ar",
    title: "Interactive 3D Simulation",
    description:
      "Play, pause, seek through the loading sequence. Zoom, rotate, inspect every layer. A virtual loading bay — not a static render. Share a link with your ops team in one click.",
    linkLabel: "Watch a demo",
    linkHref: "https://drive.google.com/file/d/1GAmyr-OY6Yt-ZdUNcKtrEoOEIHepoFAI/view",
  },
  {
    icon: "api",
    title: "API-First Architecture",
    description:
      "One REST call returns container recommendations, full packing plans, cost breakdowns, and 3D coordinates. Embed our engine in your TMS or portal in under 30 minutes.",
    linkLabel: "View API docs",
    linkHref: "/developers",
  },
];

function CapabilityCard({ icon, title, description, linkLabel, linkHref }) {
  const isExternal = linkHref.startsWith('http');
  return (
    <div className="group relative flex flex-col gap-6 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-surface-dark via-surface-dark/90 to-surface-dark p-8 transition-all hover:-translate-y-2 hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/30 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative z-10 flex size-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-cyan-500/10 text-primary transition-all group-hover:bg-gradient-to-br group-hover:from-primary group-hover:via-blue-500 group-hover:to-cyan-500 group-hover:text-white group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-primary/50">
        <span className="material-symbols-outlined text-[28px]">{icon}</span>
      </div>
      <div className="relative z-10 flex flex-col gap-3">
        <h4 className="text-xl font-black text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:via-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all">{title}</h4>
        <p className="leading-relaxed text-slate-300 group-hover:text-slate-200 transition-colors">{description}</p>
      </div>
      <a
        href={linkHref}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className="relative z-10 mt-auto inline-flex items-center gap-2 text-sm font-bold text-primary group-hover:text-cyan-400 transition-colors"
      >
        {linkLabel}
        <span className="material-symbols-outlined text-[14px] transition-transform group-hover:translate-x-1">arrow_forward</span>
      </a>
    </div>
  );
}

export function CapabilitiesSection() {
  return (
    <section id="capabilities" className="relative bg-gradient-to-b from-background-dark via-background-dark to-slate-950 py-24 overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <Container className="relative z-10">
        <div className="mx-auto mb-16 flex max-w-2xl flex-col gap-4 text-center">
          <span className="text-sm font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-1.5 rounded-full w-fit mx-auto border border-primary/20">
            What We Do
          </span>
          <h3 className="text-3xl font-black leading-tight md:text-4xl text-white">
            Beyond CBM. Beyond packing.{" "}
            <span className="bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              The full picture.
            </span>
          </h3>
          <p className="text-lg text-slate-300 font-medium">
            Three capabilities that most load planners don't have — and the ones that actually save money.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {capabilities.map((c) => (
            <CapabilityCard key={c.title} {...c} />
          ))}
        </div>
      </Container>
    </section>
  );
}
